import React, { useContext } from 'react';
import { PapiNetworkbarStrip } from '@wix/da-papi-types';
import classnames from 'classnames';
import { OverlayContextProvider } from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import MoreDeviationsBySearch from './MoreDeviationsBySearch';
import Strip from './Strip';
import { isWatchSuggestionStrip } from '../../helpers/streams';
import type { WatchSuggestionsStrip } from '../../../types/api';
import WatchSuggestionStrip from './WatchSuggestionStrip';

import s from './Strips.scss';

export interface Props {
  strips?: PapiNetworkbarStrip[];
  mainContentUrl?: string;
  originalSearchTerm?: string;
  /** Are these strips displayed at the very top of the homepage */
  areTopStrips?: boolean;
  isSearchPage?: boolean;
}

const Strips: React.FC<Props> = ({
  originalSearchTerm,
  strips,
  mainContentUrl,
  areTopStrips,
  isSearchPage,
}) => {
  const searchTerm = `'${originalSearchTerm}'`;
  const isMobile = useContext(MobileContext);

  if (strips) {
    strips = strips.filter(
      strip =>
        strip.deviations ||
        strip.collections ||
        strip.groups ||
        strip.users ||
        strip.topNecos ||
        strip.tags ||
        strip.watchSuggestions
    );
  }

  if (!strips || strips.length === 0) {
    return null;
  }

  // watch suggestions are a special snowflake, if they appear on their own dont wrap them
  if (strips.length === 1 && isWatchSuggestionStrip(strips[0])) {
    return <WatchSuggestionStrip strip={strips[0] as WatchSuggestionsStrip} />;
  }

  // if we're searching on mobile, expand the strip width to the outside of the viewport
  // so that cards in the strip slider on the right and left can always be seen
  let sliderClassName, sliderContentClassName;
  if (isMobile && isSearchPage) {
    sliderClassName = s['full-width-strips'];
    sliderContentClassName = s['full-width-strip-content'];
  }

  return (
    <div
      className={classnames(
        s['strips'],
        isSearchPage && s['on-search-page'],
        areTopStrips && s['top-strips']
      )}
    >
      <OverlayContextProvider
        value={{
          showDailyDeviationLabel: false,
          showSuggestedReasonLabel: false,
          showExtraActions: false,
        }}
      >
        {strips.map((strip, index) => (
          <BiLoggerContextProvider
            key={`${strip.title}-${index}`}
            value={{ sectionrank: index }}
          >
            <Strip
              strip={strip}
              className={s['strip']}
              sliderClassName={sliderClassName}
              sliderContentClassName={sliderContentClassName}
              isTopStrip={areTopStrips}
              isSearchPage={isSearchPage}
            />
          </BiLoggerContextProvider>
        ))}
      </OverlayContextProvider>

      {/* TODO: move this out into the page level */}
      {isSearchPage && (
        <MoreDeviationsBySearch
          searchTerm={searchTerm}
          mainContentUrl={mainContentUrl}
        />
      )}
    </div>
  );
};
Strips.displayName = 'Strips';

export default Strips;
